// Browser console ring buffer for Report It (MV-1396).
//
// MytaverseLogger (helpers/logger.ts) writes straight to the console with no
// history, so a filed report has no record of what the client logged leading up
// to the incident — including the Hub / Spatial Manager version lines logged at
// startup. This module-scoped ring buffer captures each formatted log line so
// the report can ship a recent tail.
//
// In-memory by design (mirrors rtcStatsBuffer / connectionStateStore): the
// buffer is per page-lifetime, never persisted, and scrubbed of credentials/PII
// before it leaves the browser. The backend re-scrubs + caps as defence in depth.

// A report is filed minutes — often tens of minutes — into a visit, so the tail
// has to span the session rather than the last few seconds. The BYTE cap is what
// bounds the POST /reports body (server cap is 2 MB); MAX_LINES only bounds the
// memory a long-lived chatty tab can hand to the buffer, and is set high enough
// that the byte cap is normally the one that binds.
const MAX_LINES = 20_000;
const MAX_OUTPUT_BYTES = 1024 * 1024;

// Trim in batches so the O(n) splice stays off the hot path: a busy session logs
// several lines a second, and dropping one element per call on a buffer this
// size would make the logger itself the bottleneck. The buffer therefore holds
// up to MAX_LINES + TRIM_BATCH lines, which only ever keeps extra history.
const TRIM_BATCH = 1_000;

let lines: string[] = [];

/** Append one formatted log line to the ring buffer (called from the logger). */
export function recordBrowserLogLine(line: string): void {
  if (typeof line !== 'string' || line.length === 0) {
    return;
  }

  lines.push(line);

  if (lines.length > MAX_LINES + TRIM_BATCH) {
    lines.splice(0, lines.length - MAX_LINES);
  }
}

/** Clear the buffer (tests / a fresh session). */
export function clearBrowserLogBuffer(): void {
  lines = [];
}

// Redact credentials/PII before the logs leave the browser. Order matters — the
// bearer/header forms run before the bare-JWT pass so the label survives. Kept
// in sync with the server-side backstop (report-schemas.ts scrubBrowserLogs).
const REDACTIONS: Array<[RegExp, string]> = [
  [/(authorization|bearer)\s+[A-Za-z0-9._~+/-]+=*/gi, '$1 [REDACTED]'],
  [/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, '[REDACTED_JWT]'],
  [
    /([?&](?:access_token|id_token|refresh_token|token|auth|password|secret)=)[^&\s"']+/gi,
    '$1[REDACTED]',
  ],
  // The local part is bounded at RFC 5321's 64 characters rather than left
  // open. An unbounded run has to be retried from every position inside a long
  // word before it can fail, which is quadratic: on a megabyte of stack traces
  // that is twenty seconds of frozen tab while a report is being filed.
  [
    /[A-Za-z0-9._%+-]{1,64}@[A-Za-z0-9.-]{1,255}\.[A-Za-z]{2,24}/g,
    '[REDACTED_EMAIL]',
  ],
];

/** Scrub credentials/PII from a block of log text (pure). */
export function scrubBrowserLogs(text: string): string {
  return REDACTIONS.reduce(
    (acc, [pattern, replacement]) => acc.replace(pattern, replacement),
    text,
  );
}

/**
 * How many bytes a string is as UTF-8.
 *
 * Counted rather than measured with TextEncoder, which the jsdom the tests run
 * in does not define — and a cap that cannot be exercised in a test is a cap
 * nobody trusts. Surrogate pairs are counted once, as the four bytes they
 * encode to.
 *
 * @param value - The string to measure.
 * @returns Its length in bytes.
 */
function utf8Length(value: string): number {
  let bytes = 0;

  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);

    if (code < 0x80) {
      bytes += 1;
    } else if (code < 0x800) {
      bytes += 2;
    } else if (code >= 0xd800 && code <= 0xdbff) {
      bytes += 4;
      index += 1;
    } else {
      bytes += 3;
    }
  }

  return bytes;
}

/**
 * The trailing whole lines of a block of log text that fit the byte budget.
 *
 * Whole lines only, so that a credential — which always sits within one line —
 * can never be cut into a fragment the redaction patterns no longer match, and
 * so the attachment never opens on half a line. The budget is spent in bytes
 * because the server's cap is in bytes: counting characters would under-measure
 * a non-ASCII line, and the server answers an oversized body by rejecting the
 * whole report rather than by truncating it.
 *
 * @param text - The log text to trim.
 * @returns The trailing lines that fit, or the last line alone when no whole
 *   line does.
 */
function takeTailLines(text: string): string {
  if (utf8Length(text) <= MAX_OUTPUT_BYTES) {
    return text;
  }

  const textLines = text.split('\n');
  let remaining = MAX_OUTPUT_BYTES;
  let firstKept = textLines.length - 1;

  for (let index = textLines.length - 1; index >= 0; index -= 1) {
    // Every line but the last also pays for the newline that rejoins it.
    const cost =
      utf8Length(textLines[index]) + (index === textLines.length - 1 ? 0 : 1);

    if (cost > remaining) {
      break;
    }

    remaining -= cost;
    firstKept = index;
  }

  return textLines.slice(firstKept).join('\n');
}

/**
 * The scrubbed, byte-capped log tail for a report, or undefined when empty.
 * Keeps the most recent lines when over the cap — the tail is the incident.
 *
 * Trimmed on line boundaries before scrubbing as well as after. Before, because
 * redaction is a linear scan of every character and a chatty tab can hold far
 * more text than a report will ever carry; after, because redaction can lengthen
 * what it rewrites — a label is longer than the short address it replaces.
 *
 * @returns The tail to attach to a report, or undefined when nothing was logged.
 */
export function getScrubbedBrowserLogs(): string | undefined {
  if (lines.length === 0) {
    return undefined;
  }

  const scrubbed = scrubBrowserLogs(takeTailLines(lines.join('\n')));
  const kept = takeTailLines(scrubbed);

  if (utf8Length(kept) <= MAX_OUTPUT_BYTES) {
    return kept;
  }

  // A single line longer than the entire budget, so no whole line fits. Cutting
  // into it is safe here and only here: the text has already been scrubbed, so
  // there is no credential left to split. A UTF-16 unit is at most three UTF-8
  // bytes unless it is half a surrogate pair, and a pair costs four bytes for
  // two units, so a third of the byte budget is a character count that can
  // never exceed it.
  return kept.slice(-Math.floor(MAX_OUTPUT_BYTES / 3));
}
